// v8.9.1 — Trail Config dashboard panel.
//
// Lets the user tune per-asset trail (arm % + distance %) live without
// redeploying. Loads `/api/bot/trail-config`, surfaces an editable table,
// PUTs changed rows on Save. Reset restores the compiled defaults.
//
// Each row is independent — saving only sends edited entries. Open trades
// keep their snapshotted thresholds; new trades read the updated values.
import { useEffect, useState, useCallback } from "react";
import { Loader2, RotateCcw, Save, Sliders, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ASSET_TIER = (sym) => {
  if (/^cry/.test(sym)) return { label: "Crypto", tone: "text-amber-400" };
  if (/^frxXA|^frxXP|^frxXAG|^frxXAU/.test(sym)) return { label: "Metal", tone: "text-yellow-300" };
  if (/JPY/.test(sym)) return { label: "FX·JPY", tone: "text-vts-blue" };
  if (sym === "DEFAULT") return { label: "Fallback", tone: "text-vts-silver/60" };
  return { label: "FX", tone: "text-emerald-400" };
};

export function TrailConfigPanel() {
  const [entries, setEntries] = useState([]);
  const [edits, setEdits] = useState({}); // { symbol: { arm, distance } }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/bot/trail-config");
      setEntries(data?.entries || []);
      setEdits({});
    } catch (e) {
      toast.error("Failed to load trail config", { description: e?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const rowValue = (sym, field, fallback) => {
    const e = edits[sym];
    if (e && e[field] !== undefined) return e[field];
    return fallback;
  };

  const setEdit = (sym, field, value) => {
    setEdits((cur) => ({ ...cur, [sym]: { ...(cur[sym] || {}), [field]: value } }));
  };

  const dirty = Object.keys(edits).length > 0;

  const handleSave = async () => {
    const changed = [];
    for (const row of entries) {
      const e = edits[row.symbol];
      if (!e) continue;
      const arm = parseFloat(e.arm !== undefined ? e.arm : row.arm);
      const distance = parseFloat(e.distance !== undefined ? e.distance : row.distance);
      if (Number.isNaN(arm) || Number.isNaN(distance)) {
        toast.error(`${row.symbol}: invalid arm/distance value`);
        return;
      }
      if (arm !== row.arm || distance !== row.distance) {
        changed.push({ symbol: row.symbol, arm, distance });
      }
    }
    if (changed.length === 0) {
      toast.info("Nothing to save");
      return;
    }
    setSaving(true);
    try {
      await api.put("/bot/trail-config", { entries: changed });
      toast.success(`Trail config saved · ${changed.length} ${changed.length === 1 ? "row" : "rows"}`);
      await fetchConfig();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all trail config to compiled defaults? This wipes every dashboard override.")) {
      return;
    }
    setResetting(true);
    try {
      await api.post("/bot/trail-config/reset");
      toast.success("Trail config restored to compiled defaults");
      await fetchConfig();
    } catch (e) {
      toast.error("Reset failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="trail-config-loading" className="vts-card p-5 lg:p-6">
        <div className="flex items-center gap-2 text-vts-silver/60 text-sm font-mono">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading trail config…
        </div>
      </div>
    );
  }

  return (
    <div data-testid="trail-config-panel" className="vts-card p-5 lg:p-6">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-heading font-semibold text-base text-white tracking-tight flex items-center gap-2">
            <Sliders className="h-4 w-4 text-vts-blue" />
            Trail config · per-asset arm + distance
          </h3>
          <p className="text-[11px] text-vts-silver/55 mt-1 font-mono leading-snug max-w-md">
            Adjust when the trail arms (peak ≥ arm%) and how tight the floor locks (peak − distance%).
            Saved values apply to the next opened trade — open trades keep their snapshotted thresholds.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="trail-config-reset"
            onClick={handleReset}
            disabled={resetting || saving}
            className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2.5 py-1.5 rounded border border-vts-line text-vts-silver/70 hover:border-vts-silver/50 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {resetting ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
            Reset
          </button>
          <button
            type="button"
            data-testid="trail-config-save"
            onClick={handleSave}
            disabled={!dirty || saving}
            className={`flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-3 py-1.5 rounded border transition ${
              dirty
                ? "border-vts-blue/60 bg-vts-blue/15 text-vts-blue hover:bg-vts-blue/25"
                : "border-vts-line text-vts-silver/40 cursor-not-allowed"
            } disabled:opacity-60`}
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Save {dirty ? `(${Object.keys(edits).length})` : ""}
          </button>
        </div>
      </div>

      <div className="rounded border border-vts-line overflow-hidden">
        <div className="grid grid-cols-[1fr,80px,90px,90px] text-[9px] uppercase tracking-wider font-mono text-vts-silver/50 bg-vts-bg/60 px-3 py-2 border-b border-vts-line">
          <div>Symbol</div>
          <div className="text-right">Tier</div>
          <div className="text-right">Arm %</div>
          <div className="text-right">Distance %</div>
        </div>
        <div className="divide-y divide-vts-line/50">
          {entries.map((row) => {
            const tier = ASSET_TIER(row.symbol);
            const armVal = rowValue(row.symbol, "arm", row.arm);
            const distVal = rowValue(row.symbol, "distance", row.distance);
            const isEdited = !!edits[row.symbol];
            return (
              <div
                key={row.symbol}
                data-testid={`trail-config-row-${row.symbol}`}
                className={`grid grid-cols-[1fr,80px,90px,90px] items-center px-3 py-2 text-[12px] font-mono transition-colors ${
                  isEdited ? "bg-vts-blue/5" : "hover:bg-vts-surface/40"
                }`}
              >
                <div className="text-white truncate flex items-center gap-2">
                  {isEdited && <CheckCircle2 className="h-3 w-3 text-vts-blue shrink-0" />}
                  <span>{row.symbol}</span>
                </div>
                <div className={`text-right text-[10px] uppercase tracking-wider ${tier.tone}`}>{tier.label}</div>
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="50"
                  data-testid={`trail-config-arm-${row.symbol}`}
                  value={armVal}
                  onChange={(e) => setEdit(row.symbol, "arm", e.target.value)}
                  className="bg-vts-surface border border-vts-line rounded px-2 py-1 text-right text-white text-[11px] focus:outline-none focus:border-vts-blue/60 tabular-nums w-[70px] justify-self-end"
                />
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="25"
                  data-testid={`trail-config-distance-${row.symbol}`}
                  value={distVal}
                  onChange={(e) => setEdit(row.symbol, "distance", e.target.value)}
                  className="bg-vts-surface border border-vts-line rounded px-2 py-1 text-right text-white text-[11px] focus:outline-none focus:border-vts-blue/60 tabular-nums w-[70px] justify-self-end"
                />
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-[10px] text-vts-silver/45 font-mono mt-3 leading-relaxed">
        Crypto/gold typically need wider distance (5-6%) to ride bigger swings. Forex
        stays tight (2.5%). DEFAULT applies to any symbol not listed above.
      </p>
    </div>
  );
}

export default TrailConfigPanel;
